
import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { GeneratedName } from '../types';
import { AWIN_AFFILIATE_URL_PREFIX, NAMECHEAP_SEARCH_URL_PREFIX } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { StarIcon } from './icons/StarIcon';
import { StarOutlineIcon } from './icons/StarOutlineIcon';
import { AuditIcon } from './icons/AuditIcon';

interface NameListProps {
  names: GeneratedName[];
  onToggleFavorite: (name: GeneratedName, isFavorited: boolean) => void;
}

export const NameList: React.FC<NameListProps> = ({ names, onToggleFavorite }) => {
  const { user, setModalView } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const createAffiliateUrl = (brandName: string) => {
    const namecheapUrl = `${NAMECHEAP_SEARCH_URL_PREFIX}${brandName}`;
    const affiliateUrl = `${AWIN_AFFILIATE_URL_PREFIX}${encodeURIComponent(namecheapUrl)}`;
    return affiliateUrl;
  };

  const handleFavoriteClick = (name: GeneratedName, isFavorited: boolean) => {
    if (!user) {
      setModalView('signup');
      showToast('Please sign up or log in to save names.');
      return;
    }
    onToggleFavorite(name, isFavorited);
  };

  const handleAuditClick = (brandName: string) => {
    navigate(`/brand-audit/?brand=${encodeURIComponent(brandName)}`);
  };
  
  const favoriteNames = new Set(user?.favorites?.map(f => f.name) || []);

  return (
    <ul className="space-y-3">
      {names.map((item, index) => {
        const isFavorited = favoriteNames.has(item.name);
        return (
          <li
            key={`${item.name}-${index}`}
            className="flex flex-col md:flex-row justify-between items-center p-5 bg-white hover:bg-indigo-50/30 rounded-xl border border-gray-100 shadow-sm transition-all duration-200"
          >
            <div className="flex items-center gap-3 w-full md:w-auto mb-3 md:mb-0">
               <button 
                onClick={() => handleFavoriteClick(item, isFavorited)}
                className="text-yellow-400 hover:text-yellow-500 transition-colors focus:outline-none"
                aria-label={isFavorited ? `Remove ${item.name} from favorites` : `Save ${item.name} to favorites`}
              >
                {isFavorited ? <StarIcon className="w-6 h-6" /> : <StarOutlineIcon className="w-6 h-6" />}
              </button>
              <span className="font-semibold text-lg text-[#1F2937]">{item.name}</span>
            </div>
            
            <div className="flex items-center gap-3 w-full md:w-auto justify-end">
              <button
                onClick={() => handleAuditClick(item.name)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold text-sm rounded-lg shadow-sm hover:shadow transition-all duration-300 border border-indigo-200"
                title="Audit this brand name"
              >
                <AuditIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Audit</span>
              </button>
              
              <a
                href={createAffiliateUrl(item.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-5 py-2 bg-[#10B981] hover:bg-[#059669] text-white font-semibold text-sm rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Check Domain
              </a>
            </div>
          </li>
        )
      })}
    </ul>
  );
};
